import React, { useCallback, useState } from 'react';
import { BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { IDoorInstallationModel } from '../../../../entities-generated/models/project/door-installation-model';
import { useNavigator } from '../../../../framework/services/navigation';
import { DoorType } from '../../../../entities-generated/common/door-type';
import { IInstallationSiteModel } from '../../../../entities-generated/models/project/installation-site-model';
import { toKebabCase } from '../../../../framework/utils/string';
import { AppRoutes } from '../../../app-routes';
import { ProjectService } from '../../../services/project';
import { MyProjectApiClient } from '../../../../services/my-project';
import { CustomerAccountService } from '../../../../services/customer-account';
import { ToastService } from '../../../../framework/services/toast';
import { MessageBarType } from '@fluentui/react';
import { BwjConfirmationDialog, IConfirmationDialogDisplayProps } from '../../../../framework/components/bwj-confirmation-dialog';
import { BwjEventArgs } from '../../../../framework/entities/bwj-event';
import * as CheckoutEvents from '../../events';
import { StormDoorDetailDialog } from '../../../cart/components/st-door-detail-dialog';
import { StormHandleDetailDialog } from '../../../cart/components/st-handle-detail-dialog';
import { IProjectConfiguration } from '../../../../entities-generated/models/project/project-configuration';
import { SeeInPersonDialog } from '../../../components/see-in-person-dialog';

interface ICheckoutProductModalManagerProps {
  projectConfig: IProjectConfiguration;
  onChangeViewStateRequest: (viewState: BwjViewState) => void;
}

const subscriberId = 'Component.CheckoutProductModalManager';

export const CheckoutProductModalManager: React.FunctionComponent<ICheckoutProductModalManagerProps> = (props) => {
  const nav = useNavigator();
  const { projectConfig, onChangeViewStateRequest } = props;
  const [displayedDoorDetails, setDisplayedDoorDetails] = useState<{ door: IDoorInstallationModel, site: string } | undefined>(undefined);
  const [displayedHandleDetails, setDisplayedHandleDetails] = useState<{ door: IDoorInstallationModel, site: string } | undefined>(undefined);
  const [priceWarningArgs, setPriceWarningArgs] = useState<BwjEventArgs<IConfirmationDialogDisplayProps | undefined> | undefined>(undefined);
  const [showSeeInPersonDialog, setShowSeeInPersonDialog] = useState(false);

  const onDoorSelected = (door: IDoorInstallationModel, site: IInstallationSiteModel) => {
    setDisplayedDoorDetails({ door: door, site: site.name });
    
    if(door.priceChange !== 0) {
      MyProjectApiClient.clearPriceChangeNotification(door.id)
      .then(resp => {
        CustomerAccountService.updateProjectConfiguration(resp);
      })
      .catch(() => {}); // fail silent
    }
  }
  CheckoutEvents.useDoorSelected(args => onDoorSelected(args.door, args.site), [], subscriberId);

  const onHandleSelected = (door: IDoorInstallationModel, site: IInstallationSiteModel) => {
    setDisplayedHandleDetails({ door: door, site: site.name });
  }
  CheckoutEvents.useHandleSelected(args => onHandleSelected(args.door, args.site), [], subscriberId);

  const performDoorInstallationRemoval = useCallback((door: IDoorInstallationModel) => {
    onChangeViewStateRequest(BwjViewState.Loading);

    MyProjectApiClient.removeDoorFromProject(door.id)
    .then(resp => {
      CustomerAccountService.updateProjectConfiguration(resp);
    })
    .catch(() => {
      ToastService.show(MessageBarType.error, 'Error - Please try again');
    })
    .finally(() => onChangeViewStateRequest(BwjViewState.Ready));
  }, [onChangeViewStateRequest]);

  const onRemoveDoorInstallation = (door: IDoorInstallationModel, site: IInstallationSiteModel) => {
    const numSelectedDoorsRemoved = !!door.selectedDoor ? 1 : 0;
    const numPossiblePriceIncreases =
      ProjectService.getActiveInstallationsWithSelectedDoorCount(projectConfig) - numSelectedDoorsRemoved;
    
    if(numPossiblePriceIncreases > 0) {
      setPriceWarningArgs(new BwjEventArgs({
        title: 'Price Changes',
        message: 'Our pricing is volume based, which means we are able to offer better deals when more doors are ordered. Removing this door from your project will result in slight price increases for your other selections.',
        proceedPrompt: 'Got it - proceed',
        cancelPrompt: 'Cancel',
        onProceedRequested: performDoorInstallationRemoval,
        context: door,
      }));
    } else {
      performDoorInstallationRemoval(door);
    }
  };
  CheckoutEvents.useExcludeInstallation(args => onRemoveDoorInstallation(args.door, args.site),
    [performDoorInstallationRemoval, projectConfig], subscriberId);

  const onDoorChangeRequested = (install: IDoorInstallationModel, site: string) => {
    onCloseDoorDetails();
    if(install.doorType === DoorType.StormDoor) {
      nav.goTo(AppRoutes.selectStormDoor.url(toKebabCase(site)));
    }
  };

  const onCloseDoorDetails = () => {
    setDisplayedDoorDetails(undefined);
  };

  const onHandleChangeRequested = (install: IDoorInstallationModel, site: string) => {
    onCloseHandleDetails();
    if(install.doorType === DoorType.StormDoor) {
      nav.goTo(AppRoutes.selectStormHandle.url(toKebabCase(site)));
    }
  };

  const onCloseHandleDetails = () => {
    setDisplayedHandleDetails(undefined);
  };

  return (
    <>
      <BwjConfirmationDialog
        displayArgs={priceWarningArgs}
        closeButtonAriaLabel={'Cancel removal from project and close dialog'}
      />
      <SeeInPersonDialog
        showDialog={showSeeInPersonDialog}
        onDismiss={() => setShowSeeInPersonDialog(false)}
      />
      <StormDoorDetailDialog
        info={displayedDoorDetails}
        onChangeRequested={onDoorChangeRequested}
        onCloseDialog={onCloseDoorDetails}
        onSeeInPersonRequested={() => setShowSeeInPersonDialog(true)}
      />
      <StormHandleDetailDialog
        info={displayedHandleDetails}
        onChangeRequested={onHandleChangeRequested}
        onCloseDialog={onCloseHandleDetails}
      />
    </>
  );
};
